<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">System Settings</span>
        <h1 class="hris-page-title">Cash Bond Management</h1>
        <p class="hris-page-subtitle">Track employee cash bonds, balances, and repayment status.</p>
      </div>

      <div class="hris-toolbar">
        <q-btn unelevated class="hris-q-primary" icon="fas fa-plus" label="Add New Bond" @click="resetForm(); showModal = true"></q-btn>
        <div class="hris-toolbar-spacer"></div>
        <q-select
          v-model="statusFilter"
          :options="statusOptions"
          label="Filter by Status"
          dense
          outlined
          color="green"
          style="width: 170px"
        ></q-select>
        <q-input
          v-model="searchKey"
          placeholder="Search Name or ID"
          dense
          outlined
          color="green"
          style="min-width: 220px;"
        >
          <template v-slot:append>
            <q-icon name="fas fa-search"></q-icon>
          </template>
        </q-input>
      </div>

      <div class="hris-panel">
        <q-table
          flat
          title="Employee Cash Bonds"
          :data="filteredBonds"
          :columns="columns"
          row-key="id"
          :loading="loading"
          separator="cell"
          :filter="searchKey"
        >
          <template v-slot:loading>
            <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
              <q-spinner color="green" size="40px"></q-spinner>
              <div>Loading cash bonds...</div>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="hris-empty full-width">No cash bonds found.</div>
          </template>
          <template v-slot:body-cell-actions="props">
            <q-td :props="props">
              <div class="row no-wrap q-gutter-x-sm justify-center">
                <q-btn v-if="props.row.status && props.row.status.toUpperCase() === 'ACTIVE'"
                  flat round dense class="hris-q-ghost" @click="editBond(props.row)">
                  <i class="fas fa-edit"></i>
                  <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                    <strong>Update Bond</strong>
                  </q-tooltip>
                </q-btn>
                <q-btn v-if="props.row.status && props.row.status.toUpperCase() === 'ACTIVE'"
                  flat round dense class="hris-q-ghost" @click="deleteBond(props.row.id)">
                  <i class="fas fa-trash-alt"></i>
                  <q-tooltip anchor="top middle" self="bottom middle" :offset="[10, 10]">
                    <strong>Delete Bond</strong>
                  </q-tooltip>
                </q-btn>
              </div>
            </q-td>
          </template>
          <template v-slot:body-cell-status="props">
            <q-td :props="props">
              <span class="hris-badge" :class="props.row.status && props.row.status.toUpperCase() === 'ACTIVE' ? 'hris-badge-success' : 'hris-badge-neutral'">
                {{ props.row.status }}
              </span>
            </q-td>
          </template>
        </q-table>
      </div>

      <q-dialog v-model="showModal" persistent>
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">{{ bondForm.id ? 'Edit Cash Bond' : 'Add New Cash Bond' }}</h2>
            <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost"></q-btn>
          </div>
          <div class="hris-modal-body">
            <q-form @submit="saveBond" class="q-gutter-md">
              <q-select
                outlined
                dense
                color="green"
                v-model="bondForm.employee_id"
                label="Employee"
                :options="filteredEmployeeOptions"
                use-input
                fill-input
                hide-selected
                @filter="filterEmployees"
                emit-value
                map-options
                lazy-rules
                :rules="[val => !!val || 'Required']"
                :readonly="!!bondForm.id"
              ></q-select>
              <q-input outlined dense color="green" v-model="bondForm.total_amount" label="Total Amount" type="number" lazy-rules :rules="[val => !!val || 'Required']"></q-input>
              <q-input outlined dense color="green" v-model="bondForm.remaining_balance" label="Initial Balance" type="number" lazy-rules :rules="[val => !!val || 'Required']"></q-input>

              <div class="hris-form-actions">
                <q-btn label="Cancel" unelevated class="hris-q-ghost" v-close-popup></q-btn>
                <q-btn label="Save" type="submit" unelevated class="hris-q-primary" :loading="saving"></q-btn>
              </div>
            </q-form>
          </div>
        </div>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      loading: false,
      saving: false,
      showModal: false,
      bonds: [],
      searchKey: '',
      statusFilter: 'ACTIVE',
      statusOptions: ['ALL', 'ACTIVE', 'PAID', 'COMPLETED', 'HOLD'],
      employeeOptions: [],
      filteredEmployeeOptions: [],
      bondForm: {
        id: null,
        employee_id: null,
        total_amount: '',
        remaining_balance: ''
      },
      columns: [
        { name: 'empID', label: 'Employee ID', field: 'employee_id', align: 'center' },
        { name: 'name', label: 'Name', field: 'name', align: 'left' },
        { name: 'company', label: 'Company', field: 'company', align: 'left' },
        { name: 'position', label: 'Position', field: 'position', align: 'left' },
        { name: 'total', label: 'Total Amount', field: 'total_amount', align: 'center' },
        { name: 'balance', label: 'Remaining Balance', field: 'remaining_balance', align: 'center' },
        { name: 'status', label: 'Status', field: 'status', align: 'center' },
        { name: 'actions', label: 'Actions', field: 'id', align: 'center' }
      ],
      apiUrl: process.env.VUE_APP_API_URL,
    }
  },
  computed: {
    filteredBonds() {
      let data = this.bonds;
      if (this.statusFilter && this.statusFilter !== 'ALL') {
        data = data.filter(bond => bond.status && bond.status.toUpperCase() === this.statusFilter.toUpperCase());
      }
      return data;
    }
  },
  created() {
    this.fetchBonds();
    this.fetchEmployees();
  },
  methods: {
    resetForm() {
      this.bondForm = { id: null, employee_id: null, total_amount: '', remaining_balance: '' };
    },
    filterEmployees(val, update) {
      if (val === '') {
        update(() => {
          this.filteredEmployeeOptions = this.employeeOptions;
        });
        return;
      }

      update(() => {
        const needle = val.toLowerCase();
        this.filteredEmployeeOptions = this.employeeOptions.filter(
          v => v.label.toLowerCase().indexOf(needle) > -1 || v.value.toLowerCase().indexOf(needle) > -1
        );
      });
    },
    fetchEmployees() {
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      })
      .get('/employee/list')
      .then(res => {
        this.employeeOptions = res.data.data.map(emp => {
          const parts = emp.split(' | ');
          return { label: parts[1], value: parts[0] };
        });
        this.filteredEmployeeOptions = this.employeeOptions;
      })
      .catch(() => {
        this.$q.notify({ type: 'negative', message: 'Failed to load employees' });
      });
    },
    fetchBonds() {
      this.loading = true;
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      })
      .get('/payroll/bonds')
      .then(res => {
        this.bonds = res.data.data;
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.$q.notify({ type: 'negative', message: 'Failed to load bonds' });
      });
    },
    editBond(bond) {
      this.bondForm = { ...bond };
      this.showModal = true;
    },
    deleteBond(id) {
      this.$q.dialog({
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this bond?',
        cancel: true,
        persistent: true
      }).onOk(() => {
        let formData = new FormData();
        formData.append('id', id);
        
        axios.create({
            baseURL: this.apiUrl,
            headers: { 'Authorization': `Bearer ${this.token}` }
        })
        .post('/payroll/bonds/delete', formData)
        .then(() => {
            this.fetchBonds();
            this.$q.notify({ type: 'positive', message: 'Bond deleted successfully' });
        })
        .catch(() => {
            this.$q.notify({ type: 'negative', message: 'Failed to delete bond' });
        });
      });
    },
    saveBond() {
      this.saving = true;
      let formData = new FormData();
      formData.append('employee_id', this.bondForm.employee_id);
      formData.append('total_amount', this.bondForm.total_amount);
      formData.append('remaining_balance', this.bondForm.remaining_balance);
      
      const url = this.bondForm.id ? '/payroll/bonds/update' : '/payroll/bonds/create';
      if (this.bondForm.id) formData.append('id', this.bondForm.id);

      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      })
      .post(url, formData)
      .then(() => {
        this.showModal = false;
        this.fetchBonds();
        this.$q.notify({ type: 'positive', message: 'Bond saved successfully' });
        this.saving = false;
      })
      .catch(() => {
        this.saving = false;
        this.$q.notify({ type: 'negative', message: 'Failed to save bond' });
      });
    }
  }
}
</script>
